import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link } from 'react-router-dom';
import Logo from './Logo';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ThemeToggle';
import { useLanguage } from '../context/LanguageContext';

export default function Header() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const mq = window.matchMedia('(min-width: 681px)');
    const onChange = (e) => e.matches && setOpen(false);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const links = [
    { to: '/why', label: t('nav.why') },
    { to: '/market-pulse', label: t('nav.pulse') },
    { to: '/ask', label: t('nav.ask') },
  ];

  return (
    <>
    <header className={`header ${scrolled ? 'is-scrolled' : ''} ${open ? 'is-open' : ''}`}>
      <div className="container header__inner">
        <Logo />

        <nav className="header__nav">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => (isActive ? 'active' : '')}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="header__tools">
          <div className="header__tools-desktop">
            <LanguageSelector />
            <ThemeToggle />
            <Link to="/join" className="btn btn--primary btn--sm">
              {t('nav.join')}
            </Link>
          </div>
          <button
            className="header__burger"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <span className={`header__burger-icon ${open ? 'is-open' : ''}`} aria-hidden>
              <span /><span /><span />
            </span>
          </button>
        </div>
      </div>
    </header>

    {open && createPortal(
      <>
        <button
          type="button"
          className="header__backdrop"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        />
        <div className="header__mobile" role="dialog" aria-modal="true">
          <div className="header__mobile-top" style={{ '--i': 0 }}>
            <Logo />
            <button
              type="button"
              className="header__mobile-close"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <nav className="header__mobile-nav">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                style={{ '--i': i + 1 }}
                className={({ isActive }) => (isActive ? 'active' : '')}
                onClick={() => setOpen(false)}
              >
                <span className="header__mobile-num">0{i + 1}</span>
                <span className="header__mobile-label">{l.label}</span>
                <svg className="header__mobile-chev" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </NavLink>
            ))}
          </nav>
          <div className="header__mobile-foot" style={{ '--i': links.length + 1 }}>
            <div className="header__mobile-settings">
              <div className="header__mobile-setting">
                <span>{t('nav.appearance')}</span>
                <ThemeToggle />
              </div>
              <div className="header__mobile-setting">
                <span>{t('nav.language')}</span>
                <LanguageSelector />
              </div>
            </div>
            <Link to="/join" className="btn btn--primary btn--block" onClick={() => setOpen(false)}>
              {t('nav.join')}
            </Link>
          </div>
        </div>
      </>,
      document.body
    )}
    </>
  );
}
